import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, RouteProps } from 'react-router-dom';
import { ReactElement, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ScrollToTop from './components/ScrollToTop';
import { lightTheme, darkTheme } from './theme';
import { RootState } from './store';
import AppHeader from './components/AppHeader';
import AppFooter from './components/AppFooter';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { routes } from './router/routes';
import { Suspense } from 'react';
import './i18n/config';
import { captureAttribution } from './utils/attribution';

function App() {
  const darkMode = useSelector((state: RootState) => state.theme.darkMode);
  const theme = darkMode ? darkTheme : lightTheme;

  useEffect(() => { captureAttribution(); }, []);

  // Set the document title
  useDocumentTitle();

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%',
              maxWidth: '100vw',
              margin: 0,
              padding: 0,
              paddingTop: '56px', // Fixed header height for mobile
              '@media (min-width: 600px)': {
                paddingTop: '64px' // Fixed header height for desktop
              }
            }}
          >
            <AppHeader />
            <Box 
              component="main" 
              sx={{ 
                flexGrow: 1,
                width: '100%',
                maxWidth: '100vw',
                margin: 0,
                padding: 0,
                position: 'relative',
                // `clip` rather than `hidden`: both stop horizontal overflow, but
                // `hidden` forces overflow-y to compute as `auto`, which turns this
                // element into the scrollport and stops `position: sticky`
                // (the blog table of contents) from ever engaging.
                overflowX: 'clip'
              }}
            >
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <Routes>
                  {routes.map((route: RouteProps & { children?: RouteProps[] }, index) => {
                    if (!route.children) {
                      return (
                        <Route
                          key={index}
                          path={route.path}
                          element={route.element as ReactElement}
                        />
                      );
                    }

                    return (
                      <Route
                        key={index}
                        path={route.path}
                        element={route.element as ReactElement}
                      >
                        {route.children.map((child, childIndex) => (
                          <Route
                            key={childIndex}
                            path={child.path}
                            element={child.element as ReactElement}
                          />
                        ))}
                      </Route>
                    );
                  })}
                </Routes>
              </Suspense>
            </Box>
            <AppFooter />
          </Box>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
